import { ArrowRight } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { blocksToPlainText } from '@auditwire/contracts';
import { ArticleReadingView, useArticle } from '@/features/articles';
import { NewsletterBand } from '@/features/newsletter';
import { ReactionBar } from '@/features/reactions';
import { SaveButton } from '@/features/engagement';
import { CommentSection } from '@/features/comments';
import { ErrorState, Skeleton } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { Seo, articleSchema, breadcrumbSchema, clampDescription } from '@/shared/seo';

function BackLink() {
  return (
    <Link
      to={ROUTES.home}
      className="group mb-12 flex items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowRight className="h-3 w-3 rotate-180 transition-transform group-hover:-translate-x-0.5" />
      All articles
    </Link>
  );
}

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isPending, isError, refetch } = useArticle(slug);

  if (isPending) {
    return (
      <div className="mx-auto max-w-2xl px-6 pb-20 pt-14">
        <Skeleton className="mb-6 h-4 w-24" />
        <Skeleton className="mb-4 h-14 w-full" />
        <Skeleton className="mb-8 h-6 w-3/4" />
        <Skeleton className="mb-12 aspect-[16/9] w-full rounded-3xl" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20">
        <ErrorState
          title="Couldn’t load this article"
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        {/*
          A missing article is a soft 404: the SPA fallback already returned
          HTTP 200, so `noindex` is the only way to stop Google indexing an
          empty page under a dead slug.
        */}
        <Seo title="Article not found" noIndex />
        <p className="font-display text-5xl font-black italic text-amber-500">404</p>
        <h1 className="mt-4 font-display text-2xl font-bold text-foreground">Article not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been unpublished or the link is wrong.
        </p>
        <Link
          to={ROUTES.home}
          className="mt-6 inline-block rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-zinc-950 transition-all hover:bg-amber-300"
        >
          Back to articles
        </Link>
      </div>
    );
  }

  const description = clampDescription(article.excerpt || blocksToPlainText(article.body));

  return (
    <article className="mx-auto max-w-2xl px-6 pb-20 pt-14">
      {/*
        The article's own words drive its snippet and social card. `BlogPosting`
        + `BreadcrumbList` are what make the result eligible for a byline, a
        date and a breadcrumb trail instead of a bare blue link.

        Note these tags are written after hydration. Social scrapers don't run
        JS, so `functions/articles/[slug].ts` writes the same values into the
        HTML at the edge; this keeps them correct across client-side navigation.
      */}
      <Seo
        title={article.title}
        description={description}
        path={ROUTES.article(article.slug)}
        image={article.coverUrl || undefined}
        imageAlt={article.title}
        type="article"
        article={{
          publishedTime: article.publishedAt,
          modifiedTime: article.updatedAt,
          author: article.author?.name ?? null,
          section: article.category,
        }}
        jsonLd={[
          articleSchema({
            slug: article.slug,
            title: article.title,
            excerpt: description,
            category: article.category,
            coverUrl: article.coverUrl,
            publishedAt: article.publishedAt,
            updatedAt: article.updatedAt,
            readTime: article.readTime,
            authorName: article.author?.name ?? null,
            plainText: blocksToPlainText(article.body),
          }),
          breadcrumbSchema([
            { name: 'Home', path: ROUTES.home },
            { name: article.title, path: ROUTES.article(article.slug) },
          ]),
        ]}
      />

      <BackLink />

      <ArticleReadingView
        title={article.title}
        excerpt={article.excerpt}
        category={article.category}
        coverUrl={article.coverUrl}
        body={article.body}
        readTime={article.readTime}
        publishedAt={article.publishedAt}
        author={article.author}
      />

      <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-border pt-8">
        <ReactionBar articleId={article.id} />
        <div className="ml-auto">
          <SaveButton articleId={article.id} />
        </div>
      </div>

      <div className="mt-14 border-t border-border pt-10">
        <CommentSection articleId={article.id} />
      </div>

      <div className="mt-16">
        <NewsletterBand title="Enjoyed this? Get the next one." subtitle="No spam. No paywall." />
      </div>
    </article>
  );
}
